<script setup>
/* Imports */
import {
  ref,
  onMounted,
  computed,
  watch,
} from 'vue';

/* Components */
import ProductCompareCard from '../components/ProductCompareCard.vue';
import AddProductToCompareCard from '../components/AddProductToCompareCard.vue';
import AddProductCardSkeleton from '../components/skeletons/AddProductCardSkeleton.vue';

/* Store */
import { useGlobalStore } from '../store/global.js';
import { usePricingStore } from '../store/pricing.js';

/* Analytics Helpers */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
} from '../helpers/mainHelpers.js';

/* Composables */
import { useGenericAnalyticsData } from '../composables/useAnalyticsData.js';

/* Init store */
const globalStore = useGlobalStore();
const pricingStore = usePricingStore();

/* Init composables */
const { genericAnalyticsData } = useGenericAnalyticsData();

/* Props */
const props = defineProps({
  products: {
    type: Array,
    default: () => [],
  },
  maxProducts: {
    type: Number,
    default: 2,
  },
});

/* State */
const state = ref({
  showDifferences: false,
  stickyMenuActive: false,
});

/* Show differences */
const showDifferences = (event) => {
  globalStore.setShowDifferences(event.target.checked);
};

/* Handle scroll event */
const handleScroll = () => {
  window.document.onscroll = () => {
    const { scrollY } = window;
    const appBody = document.getElementById('app-body');
    const stickyMenuWrapper = document.getElementById('sticky-menu-wrapper');
    const isStickyMenuEnabled = appBody?.offsetHeight >= 1000;
    const isStickMenuActive = scrollY > stickyMenuWrapper?.offsetTop && isStickyMenuEnabled;

    // Set sticky menu active state to true or false based on scroll position
    state.value.stickyMenuActive = isStickMenuActive;
  };
};

/* Get products */
const getProducts = (products) => products.slice(0, props.maxProducts);

const productFamily = (product) => sanitizeAnalyticsString(product?.displayName)?.split(' ')[0];

const unformattedListPrice = (pid) => pricingStore.pricingData?.find((pricing) => pricing?.id === pid)?.unformatted?.listPrice;

const unformattedSalePrice = (pid) => pricingStore.pricingData?.find((pricing) => pricing?.id === pid)?.unformatted?.salePrice;

// Computed property to determine if the "AddProductToComparCard" component should be displayed.
// It will be shown only if the current number of products is less than the maximum allowed.
const showAddProductButton = computed(() => props.products?.length < props.maxProducts);

// Computed property to calculate how many skeleton cards (AddProductCardSkeleton) to render.
// - We subtract 1 from the remaining slots if the "AddProductToComparCard" component will be shown.
// - Ensures the total number of displayed slots (products + AddProductToComparCard + AddProductCardSkeleton) never exceeds maxProducts.
const skeletonCount = computed(() => {
  const remainingSlots = props.maxProducts - (props.products?.length ?? 0);

  return showAddProductButton.value ? Math.max(0, remainingSlots - 1) : 0;
});

/* Computed - should show differences checkbox */
const shouldShowOnlyDifferencesCheckbox = computed(() => props.products?.length >= 2);

/* Computed - classes - sticky menu */
const computedStickyClass = computed(() => ({
  active: state.value.stickyMenuActive,
}));

/* Computed - classes for cards */
const cardsClasses = computed(() => ({
  cards: true, // base class
  [`columns-${props.maxProducts}`]: true, // column class based on maxProducts
}));

const sendSelectItemEvent = (payload) => {
  const { product, index } = payload;

  const analyticsData = {
    ...genericAnalyticsData(),
    item_list_name: 'Compare',
    item_list_id: 'Compare',
    index: index + 1,
    product_id: product?.pid || 'NA',
    product_brand: 'Garmin',
    product_name: sanitizeAnalyticsString(product?.displayName),
    product_original_price: unformattedListPrice(product?.pid) || 'NA',
    product_price: unformattedSalePrice(product.pid) ?? unformattedListPrice(product.pid) ?? 'NA',
    product_url: product?.productUrl || 'NA',
    item_family: productFamily(product) || 'NA',
    product_availability: product?.sellable ? 'In Stock' : 'Out Of Stock',
    is_sale: unformattedSalePrice(product?.pid) ? 'yes' : 'no',
    price_shown: unformattedListPrice(product?.pid) ? 'yes' : 'no',
    tealium_event: 'select_item',
  };

  sendAnalyticsEvent(analyticsData);
};

const handleProductCardClick = (product) => {
  sendSelectItemEvent(product);
};

const sendViewItemListEvent = (products) => {
  if (!products?.length) return;

  const analyticsData = {
    ...genericAnalyticsData(),
    item_list_name: products.map(() => 'Compare'),
    item_list_id: products.map(() => 'Compare'),
    index: products.map((_, index) => (index + 1).toString()),
    product_id: products.map((product) => product?.pid || 'NA'),
    product_brand: products.map(() => 'Garmin'),
    product_name: products.map((product) => sanitizeAnalyticsString(product?.displayName)),
    product_original_price: products.map((product) => unformattedListPrice(product?.pid) || 'NA'),
    product_price: products.map((product) => unformattedSalePrice(product.pid) ?? unformattedListPrice(product.pid) ?? 'NA'),
    product_url: products.map((product) => product?.productUrl || 'NA'),
    item_family: products.map((product) => productFamily(product) || 'NA'),
    product_availability: products.map((product) => (product?.sellable ? 'In Stock' : 'Out Of Stock')),
    is_sale: products.map((product) => (unformattedSalePrice(product?.pid) ? 'yes' : 'no')),
    price_shown: products.map((product) => (unformattedListPrice(product?.pid) ? 'yes' : 'no')),
    tealium_event: 'view_item_list',
  };

  sendAnalyticsEvent(analyticsData);
};

/* Mount */
onMounted(() => {
  handleScroll();

  window.addEventListener('utag-ready', () => {
    sendViewItemListEvent(props.products);
  });
});

// Fire 'view_item_list' on product add/remove
watch(
  () => props.products,
  async (newProducts, oldProducts) => {
    // Exit early if there are no products or if the products haven't changed
    if (!newProducts?.length || newProducts === oldProducts) return;

    // Wait for all product prices to be loaded
    await Promise.all(
      newProducts.map(async (product) => {
        // If the price already exists, do nothing
        const existing = pricingStore?.pricingData?.find((p) => p?.id === product?.pid);

        // If no price exists yet, fetch the price
        if (!existing) {
          await pricingStore.fetchPriceForProduct(product?.pid);
        }
      }),
    );

    // All prices are loaded, send the analytics event
    sendViewItemListEvent(newProducts);
  },
);
</script>

<template>
  <div id="sticky-menu-wrapper">
    <div
      id="sticky-menu"
      class="sticky-menu"
      :class="computedStickyClass"
    >
      <div class="container-inner">
        <div
          :class="cardsClasses"
          data-testid="sticky-menu-cards"
        >
          <ProductCompareCard
            v-for="(card, index) in getProducts(props.products)"
            :key="card.pid"
            :card="card"
            :data-test-pid="card.pid"
            class="card"
            @card-click="handleProductCardClick({
              product: card,
              index,
            })"
          />
          <AddProductToCompareCard
            v-if="showAddProductButton"
            :sticky-menu-active="state.stickyMenuActive"
            :max-products="props.maxProducts"
          />
          <AddProductCardSkeleton
            v-for="skeleton in skeletonCount"
            :key="'add-product-card-skeleton-' + skeleton"
          />
        </div>

        <!-- 'Teleport' target placeholder for the Sticky Autocomplete variant -->
        <div id="sticky-search-target" />

        <div
          v-if="shouldShowOnlyDifferencesCheckbox"
          class="sticky-menu__checkbox"
        >
          <g-checkbox :label="globalStore.getTranslations?.ITFE_PRODUCT_COMPARE_ONLY_DIFFERENCES_MESSAGE">
            <input
              v-model="state.showDifferences"
              name="showDifferences"
              type="checkbox"
              value="1"
              tabindex="0"
              @change="showDifferences($event)"
            >
          </g-checkbox>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#sticky-menu-wrapper {
  min-height: 180px;
}

.sticky-menu {
  background-color: $color-white;
  z-index: 1000;
  padding: 1rem 0;
  position: relative;
  transition: all 0.2s;

  :deep(.g__checkbox__label) {
    @include font-primary-weight-regular();
  }

  // Scrolled sticky menu styles
  &.active {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    @include make-drop-shadow();
    background-color: $color-white;

    @media print {
      position: relative;
    }
  }

  // Vertical stacking
  .container-inner {
    display: flex;
    flex-direction: column;

    // Vertical spacing between visible elements
    > * + * {
      padding-top: 1rem;
    }
  }

  // Remove placeholder for the empty search
  #sticky-search-target:empty {
    display: none;
  }
}

.cards {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  gap: 1rem;

  &.columns-5 {

    .card {
      flex-basis: calc(20% - 0.8rem);
    }
  }

  &.columns-4 {

    .card {
      flex-basis: calc(25% - 0.75rem);
    }
  }

  &.columns-3 {

    .card {
      flex-basis: calc(33.33% - 0.67rem);
    }
  }

  &.columns-2 {

    .card {
      flex-basis: calc(50% - 0.5rem);
    }
  }
}
</style>
